import { useCallback } from 'react'

import { FileItem } from '@inno-sdks/files'
import { UNKNOWN_FILE_EXTENSION } from '@inno-sdks/mime-types'
import { useQueryClient } from '@tanstack/react-query'

import { getFileItemQueryOptions } from '../../api/hooks/useGetFileItem'
import { useDownloadFileMutation } from '../../api/requests/downloadFile'

type UseDownloadFileBaseOptions = {
  abortController?: AbortController
  onProgress?(progress: number): void
}

export const useDownloadFileBase = () => {
  const { mutateAsync } = useDownloadFileMutation()
  const queryClient = useQueryClient()

  return useCallback(
    async (
      itemId: number,
      { abortController, onProgress }: UseDownloadFileBaseOptions = {}
    ) => {
      const item = await queryClient.fetchQuery(
        getFileItemQueryOptions<FileItem>(itemId)
      )

      if (!item) return undefined

      const blob = await mutateAsync({
        signedUrl: item.metaData.presignedUrl!,
        mimeType: item.metaData.mimeType,
        onProgress: progress => onProgress?.(progress),
        abortController
      })

      let downloadFileName = item.name

      if (
        item.metaData.extension &&
        item.name.includes('.') &&
        `.${item.metaData.extension}` !== `.${UNKNOWN_FILE_EXTENSION}` &&
        item.name.split('.').at(-1) !== item.metaData.extension
      ) {
        downloadFileName = `${downloadFileName}.${item.metaData.extension}`
      }

      return new File([blob], downloadFileName, { type: blob.type })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
}
